/*
 This file is part of GNU Taler
 (C) 2022-2024, 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import { Duration } from "@gnu-taler/taler-util";
import {
  AbsoluteTime,
  AccessToken,
  Codec,
  buildCodecForObject,
  buildCodecForUnion,
  codecForAbsoluteTime,
  codecForBoolean,
  codecForConstString,
  codecForString,
  codecOptionalDefault,
} from "@gnu-taler/taler-util";
import {
  buildStorageKey,
  useBankCoreApiContext,
  useLocalStorage,
} from "@gnu-taler/web-util/browser";
import { mutate } from "swr";
import { SESSION_DURATION } from "../pages/LoginForm.js";
import { createRFC8959AccessTokenEncoded } from "@gnu-taler/taler-util";
import { useEffect, useRef } from "preact/hooks";

/**
 * Has the information to reach and
 * authenticate at the bank's backend.
 */
export type SessionState = LoggedIn | LoggedOut | Expired;

export interface LoggedIn {
  status: "loggedIn";
  isUserAdministrator: boolean;
  username: string;
  token: AccessToken;
  expiration: AbsoluteTime;
}
interface Expired {
  status: "expired";
  isUserAdministrator: boolean;
  username: string;
  expiration: AbsoluteTime;
}
interface LoggedOut {
  status: "loggedOut";
}

export const codecForSessionStateLoggedIn = (): Codec<LoggedIn> =>
  buildCodecForObject<LoggedIn>()
    .property("status", codecForConstString("loggedIn"))
    .property("username", codecForString())
    .property(
      "expiration",
      codecOptionalDefault(codecForAbsoluteTime, AbsoluteTime.now()),
    )
    .property("token", codecForString() as unknown as Codec<AccessToken>)
    .property("isUserAdministrator", codecForBoolean())
    .build("SessionState.LoggedIn");

export const codecForSessionStateExpired = (): Codec<Expired> =>
  buildCodecForObject<Expired>()
    .property("status", codecForConstString("expired"))
    .property("username", codecForString())
    .property(
      "expiration",
      codecOptionalDefault(codecForAbsoluteTime, AbsoluteTime.now()),
    )
    .property("isUserAdministrator", codecForBoolean())
    .build("SessionState.Expired");

export const codecForSessionStateLoggedOut = (): Codec<LoggedOut> =>
  buildCodecForObject<LoggedOut>()
    .property("status", codecForConstString("loggedOut"))
    .build("SessionState.LoggedOut");

export const codecForSessionState = (): Codec<SessionState> =>
  buildCodecForUnion<SessionState>()
    .discriminateOn("status")
    .alternative("loggedIn", codecForSessionStateLoggedIn())
    .alternative("loggedOut", codecForSessionStateLoggedOut())
    .alternative("expired", codecForSessionStateExpired())
    .build("SessionState");

export const defaultState: SessionState = {
  status: "loggedOut",
};

export interface SessionStateHandler {
  state: SessionState;
  logOut(): void;
  expired(): void;
  logIn(info: {
    username: string;
    token: AccessToken;
    expiration: AbsoluteTime;
  }): void;
}

const SESSION_STATE_KEY = buildStorageKey(
  "bank-session",
  codecForSessionState(),
);

/**
 * Return getters and setters for
 * login credentials and backend's
 * base URL.
 */
export function useSessionState(): SessionStateHandler {
  const { value: state, update } = useLocalStorage(
    SESSION_STATE_KEY,
    defaultState,
  );
  const updateRef = useRef(update);
  updateRef.current = update;
  const loggedInUsername =
    state.status === "loggedIn" ? state.username : undefined;
  const loggedInExpiration =
    state.status === "loggedIn" ? state.expiration.t_ms : undefined;
  const loggedInIsAdmin =
    state.status === "loggedIn" ? state.isUserAdministrator : undefined;

  useEffect(() => {
    if (
      loggedInUsername === undefined ||
      loggedInExpiration === undefined ||
      loggedInExpiration === "never"
    ) {
      return;
    }
    const expiration = AbsoluteTime.fromMilliseconds(loggedInExpiration);
    const remaining = Duration.getRemaining(expiration);
    if (remaining.d_ms === "forever") return;
    const timeout = setTimeout(
      () => {
        const nextState: SessionState = {
          status: "expired",
          username: loggedInUsername,
          expiration,
          isUserAdministrator: loggedInIsAdmin ?? false,
        };
        updateRef.current(nextState);
      },
      Math.max(remaining.d_ms, 0),
    );
    return () => clearTimeout(timeout);
  }, [loggedInExpiration, loggedInIsAdmin, loggedInUsername]);

  return {
    state,
    logOut() {
      update(defaultState);
    },
    expired() {
      if (state.status === "loggedOut") return;
      const nextState: SessionState = {
        status: "expired",
        username: state.username,
        expiration: state.expiration,
        isUserAdministrator: state.username === "admin",
      };
      update(nextState);
    },
    logIn(info) {
      // admin is defined by the username
      const nextState: SessionState = {
        status: "loggedIn",
        ...info,
        isUserAdministrator: info.username === "admin",
      };
      update(nextState);
      cleanAllCache();
    },
  };
}

function cleanAllCache(): void {
  mutate(() => true, undefined, { revalidate: false });
}

export function refreshRetryDelay(timeLeftMs: number, attempt: number): number {
  return Math.min(1000 * 2 ** attempt, 30_000, timeLeftMs);
}

/**
 * Loads the session from local storage
 * Sets a timeout before the session expires
 * Makes a request to refresh the session
 * Saves new session
 */
export function useRefreshSessionBeforeExpires() {
  const session = useSessionState();
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const {
    lib: { bank },
  } = useBankCoreApiContext();

  const refreshSession =
    session.state.status !== "loggedIn" ||
    session.state.expiration.t_ms === "never"
      ? undefined
      : session.state;
  const refreshUsername = refreshSession?.username;
  const refreshToken = refreshSession?.token;
  const refreshExpiration = refreshSession?.expiration.t_ms;

  useEffect(() => {
    if (
      refreshUsername === undefined ||
      refreshToken === undefined ||
      refreshExpiration === undefined ||
      refreshExpiration === "never"
    ) {
      return;
    }
    const expiration = AbsoluteTime.fromMilliseconds(refreshExpiration);
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    /**
     * we need to wait before refreshing the session. Waiting too much and the token will
     * be expired. So 20% before expiration should be close enough.
     */
    const timeLeftBeforeExpiration = Duration.getRemaining(expiration);
    const refreshWindow = Duration.multiply(
      Duration.fromTalerProtocolDuration(SESSION_DURATION),
      0.2,
    );
    if (
      timeLeftBeforeExpiration.d_ms === "forever" ||
      refreshWindow.d_ms === "forever"
    )
      return;
    const remain = Math.max(
      timeLeftBeforeExpiration.d_ms - refreshWindow.d_ms,
      0,
    );

    const attemptRefresh = async (attempt: number): Promise<void> => {
      if (cancelled) return;
      try {
        const result = await bank.createAccessToken(
          refreshUsername,
          { type: "bearer", token: refreshToken },
          {
            scope: "readwrite",
            duration: SESSION_DURATION,
            refreshable: true,
          },
        );
        if (cancelled) return;
        if (result.type === "ok") {
          sessionRef.current.logIn({
            username: refreshUsername,
            token: createRFC8959AccessTokenEncoded(result.body.access_token),
            expiration: AbsoluteTime.fromProtocolTimestamp(
              result.body.expiration,
            ),
          });
          return;
        }
        // createAccessToken returns only documented permanent failures
        // (unauthorized, forbidden/locked, or missing account).  Network and
        // unexpected server failures throw and are retried below.
        sessionRef.current.expired();
        return;
      } catch {
        // Network failures are transient while the current token remains valid.
      }

      const left = Duration.getRemaining(expiration);
      if (left.d_ms === "forever" || left.d_ms <= 0) {
        sessionRef.current.expired();
        return;
      }
      const retryDelay = refreshRetryDelay(left.d_ms, attempt);
      timeoutId = setTimeout(
        () => void attemptRefresh(attempt + 1),
        retryDelay,
      );
    };

    timeoutId = setTimeout(() => void attemptRefresh(0), remain);
    return () => {
      cancelled = true;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [bank, refreshUsername, refreshToken, refreshExpiration]);
}
